import { canEncodeAudio, canEncodeVideo, Quality } from "mediabunny";
import { hasOpfs } from "../storage/opfs";

export type CapabilityReport = {
  secureContext: boolean;
  webCodecs: boolean;
  audioDecoder: boolean;
  audioEncoder: boolean;
  videoEncoder: boolean;
  h264: boolean;
  nativeAac: boolean;
  aac: boolean;
  aacMode: "native" | "wasm" | "unavailable";
  opfs: boolean;
  webShare: boolean;
  wakeLock: boolean;
  serviceWorker: boolean;
};

let aacFallbackRegistered = false;

export async function inspectCapabilities(
  sampleRate = 48_000,
  channels = 2,
): Promise<CapabilityReport> {
  const nativeAac = await supportsNativeAac(sampleRate, channels);
  if (
    !nativeAac &&
    typeof WebAssembly !== "undefined" &&
    !aacFallbackRegistered
  ) {
    const { registerAacEncoder } = await import("@mediabunny/aac-encoder");
    registerAacEncoder();
    aacFallbackRegistered = true;
  }
  const [h264, aac] = await Promise.all([
    canEncodeVideo("avc", {
      width: 1280,
      height: 720,
      quality: new Quality({ bitrate: 250_000 }),
      fullCodecString: "avc1.42001f",
      hardwareAcceleration: "no-preference",
    }).catch(() => false),
    canEncodeAudio("aac", {
      sampleRate,
      numberOfChannels: channels,
      quality: new Quality({ bitrate: 192_000 }),
    }).catch(() => false),
  ]);
  return {
    secureContext: globalThis.isSecureContext,
    webCodecs:
      typeof globalThis.VideoEncoder !== "undefined" &&
      typeof globalThis.AudioData !== "undefined",
    audioDecoder: typeof globalThis.AudioDecoder !== "undefined",
    audioEncoder: typeof globalThis.AudioEncoder !== "undefined",
    videoEncoder: typeof globalThis.VideoEncoder !== "undefined",
    h264,
    nativeAac,
    aac,
    aacMode: nativeAac ? "native" : aac ? "wasm" : "unavailable",
    opfs: hasOpfs(),
    webShare: typeof navigator.share === "function",
    wakeLock: typeof navigator.wakeLock?.request === "function",
    serviceWorker: "serviceWorker" in navigator,
  };
}

export function exportCapabilityError(report: CapabilityReport): string | null {
  if (!report.secureContext)
    return "動画生成にはHTTPSまたはlocalhostでの起動が必要です";
  if (!report.audioDecoder)
    return "動画生成に必要なAudioDecoderがこの端末では利用できません";
  if (!report.videoEncoder || !report.h264)
    return "動画生成に必要なH.264エンコーダがこの端末では利用できません";
  if (!report.aac)
    return "動画生成に必要なAACエンコーダがこの端末では利用できません";
  if (!report.opfs)
    return "長時間動画の一時保存に必要なOPFSがこの端末では利用できません";
  return null;
}

async function supportsNativeAac(
  sampleRate: number,
  channels: number,
): Promise<boolean> {
  if (typeof globalThis.AudioEncoder === "undefined") return false;
  try {
    const result = await globalThis.AudioEncoder.isConfigSupported({
      codec: "mp4a.40.2",
      sampleRate,
      numberOfChannels: channels,
      bitrate: 192_000,
    });
    return result.supported === true;
  } catch {
    return false;
  }
}
